import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import ExamTypeSection from './ExamTypeSection';
import MaternalStructureTabs, { createMaternalStructures } from './MaternalStructureTabs';
import {
  generateReportFiles, saveReportFiles, describeSavedReport,
  reservePreviewWindow, abandonPreviewWindow, showReportPreview, postReportAudit
} from '../generators/reportOutput';
import { useTechnologists } from '../hooks/useTechnologists';

// Initial form data — factory function so "Clear Form" can get a fresh copy,
// not just re-reference the object the component mounted with.
function getInitialFormData() {
  return {
    examType: 'Second Trimester OB',
    examDate: new Date().toISOString().split('T')[0],
    firstName: '',
    lastName: '',
    mrn: '',
    technologist: '',
    reason: 'Anatomy',
    callReport: false,
    phoneNumber: '',
    history: '',
    comments: '',
    examTypeDetails: {
      transabdominal: true,
      transvaginal: false,
      transvaginalOnly: false
    },
    numberOfFetuses: 1,
    chorionicity: '',
    // Shared sections
    maternalStructures: createMaternalStructures(),
    // Per-fetus data array
    fetuses: [
      // Fetus 1
      {
        id: 1,
        examMode: 'anatomic',
        presentation: 'Cephalic',
        maternalLocation: 'Maternal Right',
        fetalHeartRate: '',
        placentaLocation: 'Anterior',
        placentaGrade: '0',
        placentaPrevia: 'None',
        umbilicalCord: '3 vessel cord',
        gender: 'Not determined',
        amnioticFluid: 'Normal',
        afi: '',
        biometry: {
          bpd: { size: '', weeks: '', days: '', percentile: '' },
          hc: { size: '', weeks: '', days: '', percentile: '' },
          ac: { size: '', weeks: '', days: '', percentile: '' },
          fl: { size: '', weeks: '', days: '', percentile: '' },
          efw: '',
          percentile: '',
          ci: '',
          hcac: '',
          flbpd: '',
          flhc: '',
          flac: ''
        },
        anatomy: {
          nasalBone: 'Seen - Normal',
          profile: 'Seen - Normal',
          noseLips: 'Seen - Normal',
          face: 'Seen - Normal',
          palate: 'Seen - Normal',
          mandibleChin: 'Seen - Normal',
          orbits: 'Seen - Normal',
          ventricles: 'Seen - Normal',
          choroidPlexus: 'Seen - Normal',
          midlineFalx: 'Seen - Normal',
          csp: 'Seen - Normal',
          cerebellum: 'Seen - Normal',
          cisternaMagna: 'Seen - Normal',
          nuchalFold: 'Seen - Normal',
          fetalCI: 'Seen - Normal',
          placentaCI: 'Seen - Normal',
          bladder: 'Seen - Normal',
          kidneys: 'Seen - Normal',
          openHands: 'Seen - Normal',
          arms: 'Seen - Normal',
          feetAnkle: 'Seen - Normal',
          legs: 'Seen - Normal',
          diaphragm: 'Seen - Normal',
          stomach: 'Seen - Normal',
          cSpine: 'Seen - Normal',
          tSpine: 'Seen - Normal',
          lSpine: 'Seen - Normal',
          sSpine: 'Seen - Normal',
          fourCH: 'Seen - Normal',
          cardiacAxis: 'Seen - Normal',
          aorta: 'Seen - Normal',
          ductalArch: 'Seen - Normal',
          heartSweep: 'Seen - Normal',
          outletCross: 'Seen - Normal',
          aortaRA: 'Seen - Normal',
          lvot: 'Seen - Normal',
          rvot: 'Seen - Normal',
          threeVV: 'Seen - Normal',
          threeCord: 'Seen - Normal',
          situs: 'Seen - Normal',
          levelII: false,
          humeralLength: '',
          hipAngle: '',
          fifthDigits: 'Seen - Normal'
        },
        gestationalAge: {
          ultrasoundEGA: { weeks: '', days: '' },
          ultrasoundEDD: '',
          clinicalEGA: { weeks: '', days: '' },
          clinicalEDD: '',
          lmp: ''
        },
        biophysicalProfile: {
          breathing: '',
          movement: '',
          tone: '',
          fluid: ''
        },
        uaDoppler: {
          fetal: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
          midCord: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
          placenta: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' }
        },
        comments: ''
      }
    ]
  };
}

function SecondTrimesterMultipleOBForm({ token, onBack, facilityInfo }) {
  // Initial state with number of fetuses selection
  const [numberOfFetuses, setNumberOfFetuses] = useState(1); // Default to singleton
  const [activeFetusTab, setActiveFetusTab] = useState(0); // 0-indexed
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  const [formData, setFormData] = useState(getInitialFormData);

  useEffect(() => {
   if (defaultTech && !formData.technologist) {
    setFormData(prev => ({ ...prev, technologist: defaultTech }));
   }
  }, [defaultTech, formData.technologist]);

  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedAnatomyField, setFocusedAnatomyField] = useState(null);
  const [focusedDiastolicField, setFocusedDiastolicField] = useState(null);
  const [focusedBPPField, setFocusedBPPField] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState(0); // 0=Fetal Env, 1=Biometry, 2=Anatomy, 3=BPP, 4=UA Doppler
  const [activeMaternalTab, setActiveMaternalTab] = useState(0);
  const [showReferenceRange, setShowReferenceRange] = useState(false);

  // Helper function to get current fetus data
  const currentFetus = formData.fetuses[activeFetusTab];

  // Date formatting helper
  const formatDate = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  // Handle EGA changes
  const handleEGAChange = (egaField, subField, value) => {
    const numbers = value.replace(/\D/g, '');
    updateCurrentFetusNested('gestationalAge', {
      [egaField]: {
        ...currentFetus.gestationalAge[egaField],
        [subField]: numbers
      }
    });
  };

  // Get background color for anatomy dropdown based on value and focus state
  const getAnatomyDropdownStyle = (fieldName, value) => {
    // If this dropdown is focused (opened), return white background
    if (focusedAnatomyField === fieldName) {
      return { backgroundColor: 'var(--color-input-bg)' };
    }
    
    // Otherwise, return color-coded background based on value
    if (value === 'Abnormal') {
      return { backgroundColor: 'var(--color-status-bad-bg)' }; // Light red
    } else if (value === 'Not Well Visualized') {
      return { backgroundColor: 'var(--color-status-info-bg)' }; // Light blue
    } else if (value === 'Seen - Normal') {
      return { backgroundColor: 'var(--color-status-ok-bg)' }; // Light green
    } else if (value === 'Not Evaluated') {
      return { backgroundColor: 'var(--color-status-neutral-bg)' }; // Gray
    }
    return {};
  };

  // Get background color for diastolic flow dropdown based on value and focus state
  const getDiastolicFlowStyle = (fieldName, value) => {
    // If this dropdown is focused (opened), return white background
    if (focusedDiastolicField === fieldName) {
      return { backgroundColor: 'var(--color-input-bg)' };
    }
    
    // Otherwise, return color-coded background based on value
    if (value === 'Present') {
      return { backgroundColor: 'var(--color-status-ok-bg)' }; // Green
    } else if (value === 'Absent' || value === 'Reversed') {
      return { backgroundColor: 'var(--color-status-bad-bg)' }; // Red
    }
    return { backgroundColor: 'var(--color-input-bg)' }; // Default/empty
  };

  // Get background color for BPP dropdown based on value and focus state
  const getBPPDropdownStyle = (fieldName, value) => {
    // If this dropdown is focused (opened), return white background
    if (focusedBPPField === fieldName) {
      return { backgroundColor: 'var(--color-input-bg)' };
    }
    
    // Otherwise, return color-coded background based on value
    if (value === 'Present' || value === 'Adequate') {
      return { backgroundColor: 'var(--color-status-ok-bg)' }; // Green (2 points)
    } else if (value === 'Absent' || value === 'Decreased') {
      return { backgroundColor: 'var(--color-status-bad-bg)' }; // Red (0 points)
    }
    return { backgroundColor: 'var(--color-input-bg)' }; // Default/empty
  };

  // Calculate BPP total score
  const calculateBPPScore = () => {
    const bpp = currentFetus.biophysicalProfile;
    let total = 0;
    
    if (bpp.breathing === 'Present') total += 2;
    if (bpp.movement === 'Present') total += 2;
    if (bpp.tone === 'Present') total += 2;
    if (bpp.fluid === 'Adequate') total += 2;
    
    return total;
  };

  // Calculate gestation concordance
  const calculateGestationConcordance = () => {
    const usWeeks = parseInt(currentFetus.gestationalAge.ultrasoundEGA?.weeks) || 0;
    const usDays = parseInt(currentFetus.gestationalAge.ultrasoundEGA?.days) || 0;
    const clinicalWeeks = parseInt(currentFetus.gestationalAge.clinicalEGA.weeks) || 0;
    const clinicalDays = parseInt(currentFetus.gestationalAge.clinicalEGA.days) || 0;

    if (!usWeeks && !usDays && !clinicalWeeks && !clinicalDays) {
      return '';
    }
    if ((!usWeeks && !usDays) || (!clinicalWeeks && !clinicalDays)) {
      return '';
    }

    const usTotalDays = (usWeeks * 7) + usDays;
    const clinicalTotalDays = (clinicalWeeks * 7) + clinicalDays;
    const difference = usTotalDays - clinicalTotalDays;

    if (difference === 0) {
      return 'Size concordant with dates';
    }

    const absDiff = Math.abs(difference);
    const weeks = Math.floor(absDiff / 7);
    const days = absDiff % 7;

    let text = 'Size ';
    
    if (weeks > 0 && days > 0) {
      text += `${weeks} week${weeks > 1 ? 's' : ''} ${days} day${days > 1 ? 's' : ''}`;
    } else if (weeks > 0) {
      text += `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else {
      text += `${days} day${days > 1 ? 's' : ''}`;
    }

    if (difference > 0) {
      text += ' ahead of dates';
    } else {
      text += ' less than dates';
    }

    return text;
  };

  // Helper function to update current fetus data
  const updateCurrentFetus = (updates) => {
    setFormData(prev => ({
      ...prev,
      fetuses: prev.fetuses.map((fetus, index) =>
        index === activeFetusTab ? { ...fetus, ...updates } : fetus
      )
    }));
  };

  // Helper function to update nested fetus data
  const updateCurrentFetusNested = (section, updates) => {
    setFormData(prev => ({
      ...prev,
      fetuses: prev.fetuses.map((fetus, index) =>
        index === activeFetusTab
          ? { ...fetus, [section]: { ...fetus[section], ...updates } }
          : fetus
      )
    }));
  };

  // Set anatomy defaults based on exam mode
  const setAnatomyDefaults = (mode) => {
    // Update the exam mode state
    updateCurrentFetus({ examMode: mode });
    
    if (mode === 'anatomic') {
      // Anatomic Survey - all fields to "Seen - Normal"
      updateCurrentFetusNested('anatomy', {
        nasalBone: 'Seen - Normal',
        profile: 'Seen - Normal',
        noseLips: 'Seen - Normal',
        face: 'Seen - Normal',
        palate: 'Seen - Normal',
        mandibleChin: 'Seen - Normal',
        orbits: 'Seen - Normal',
        ventricles: 'Seen - Normal',
        choroidPlexus: 'Seen - Normal',
        midlineFalx: 'Seen - Normal',
        csp: 'Seen - Normal',
        cerebellum: 'Seen - Normal',
        cisternaMagna: 'Seen - Normal',
        nuchalFold: 'Seen - Normal',
        fetalCI: 'Seen - Normal',
        placentaCI: 'Seen - Normal',
        bladder: 'Seen - Normal',
        kidneys: 'Seen - Normal',
        openHands: 'Seen - Normal',
        arms: 'Seen - Normal',
        feetAnkle: 'Seen - Normal',
        legs: 'Seen - Normal',
        diaphragm: 'Seen - Normal',
        stomach: 'Seen - Normal',
        cSpine: 'Seen - Normal',
        tSpine: 'Seen - Normal',
        lSpine: 'Seen - Normal',
        sSpine: 'Seen - Normal',
        fourCH: 'Seen - Normal',
        cardiacAxis: 'Seen - Normal',
        aorta: 'Seen - Normal',
        ductalArch: 'Seen - Normal',
        heartSweep: 'Seen - Normal',
        outletCross: 'Seen - Normal',
        aortaRA: 'Seen - Normal',
        lvot: 'Seen - Normal',
        rvot: 'Seen - Normal',
        threeVV: 'Seen - Normal',
        threeCord: 'Seen - Normal',
        situs: 'Seen - Normal',
        fifthDigits: 'Seen - Normal'
      });
    } else if (mode === 'followup') {
      // Follow Up - only bladder, kidneys, stomach, fourCH to "Seen - Normal", rest to "Not Evaluated"
      updateCurrentFetusNested('anatomy', {
        nasalBone: 'Not Evaluated',
        profile: 'Not Evaluated',
        noseLips: 'Not Evaluated',
        face: 'Not Evaluated',
        palate: 'Not Evaluated',
        mandibleChin: 'Not Evaluated',
        orbits: 'Not Evaluated',
        ventricles: 'Not Evaluated',
        choroidPlexus: 'Not Evaluated',
        midlineFalx: 'Not Evaluated',
        csp: 'Not Evaluated',
        cerebellum: 'Not Evaluated',
        cisternaMagna: 'Not Evaluated',
        nuchalFold: 'Not Evaluated',
        fetalCI: 'Not Evaluated',
        placentaCI: 'Not Evaluated',
        bladder: 'Seen - Normal',
        kidneys: 'Seen - Normal',
        openHands: 'Not Evaluated',
        arms: 'Not Evaluated',
        feetAnkle: 'Not Evaluated',
        legs: 'Not Evaluated',
        diaphragm: 'Not Evaluated',
        stomach: 'Seen - Normal',
        cSpine: 'Not Evaluated',
        tSpine: 'Not Evaluated',
        lSpine: 'Not Evaluated',
        sSpine: 'Not Evaluated',
        fourCH: 'Seen - Normal',
        cardiacAxis: 'Not Evaluated',
        aorta: 'Not Evaluated',
        ductalArch: 'Not Evaluated',
        heartSweep: 'Not Evaluated',
        outletCross: 'Not Evaluated',
        aortaRA: 'Not Evaluated',
        lvot: 'Not Evaluated',
        rvot: 'Not Evaluated',
        threeVV: 'Not Evaluated',
        threeCord: 'Not Evaluated',
        situs: 'Not Evaluated',
        fifthDigits: 'Not Evaluated'
      });
    }
  };

  const handleMaternalChange = (changes) => {
    setFormData(prev => ({
      ...prev,
      maternalStructures: { ...prev.maternalStructures, ...changes }
    }));
  };

  // Handle number of fetuses change
  const handleNumberOfFetusesChange = (num) => {
    const newNum = parseInt(num);
    setNumberOfFetuses(newNum);
    
    // examType stays constant as 'Second Trimester OB'
    const newExamType = 'Second Trimester OB';
    
    // Create template for new fetus
    const createFetus = (id) => ({
      id,
      examMode: 'anatomic',
      presentation: 'Cephalic',
      maternalLocation: id === 1 ? 'Maternal Right' : 'Maternal Left',
      fetalHeartRate: '',
      placentaLocation: 'Anterior',
      placentaGrade: '0',
      placentaPrevia: 'None',
      umbilicalCord: '3 vessel cord',
      gender: 'Not determined',
      amnioticFluid: 'Normal',
      afi: '',
      biometry: {
        bpd: { size: '', weeks: '', days: '', percentile: '' },
        hc: { size: '', weeks: '', days: '', percentile: '' },
        ac: { size: '', weeks: '', days: '', percentile: '' },
        fl: { size: '', weeks: '', days: '', percentile: '' },
        efw: '',
        percentile: '',
        ci: '',
        hcac: '',
        flbpd: '',
        flhc: '',
        flac: ''
      },
      anatomy: {
        nasalBone: 'Seen - Normal',
        profile: 'Seen - Normal',
        noseLips: 'Seen - Normal',
        face: 'Seen - Normal',
        palate: 'Seen - Normal',
        mandibleChin: 'Seen - Normal',
        orbits: 'Seen - Normal',
        ventricles: 'Seen - Normal',
        choroidPlexus: 'Seen - Normal',
        midlineFalx: 'Seen - Normal',
        csp: 'Seen - Normal',
        cerebellum: 'Seen - Normal',
        cisternaMagna: 'Seen - Normal',
        nuchalFold: 'Seen - Normal',
        fetalCI: 'Seen - Normal',
        placentaCI: 'Seen - Normal',
        bladder: 'Seen - Normal',
        kidneys: 'Seen - Normal',
        openHands: 'Seen - Normal',
        arms: 'Seen - Normal',
        feetAnkle: 'Seen - Normal',
        legs: 'Seen - Normal',
        diaphragm: 'Seen - Normal',
        stomach: 'Seen - Normal',
        cSpine: 'Seen - Normal',
        tSpine: 'Seen - Normal',
        lSpine: 'Seen - Normal',
        sSpine: 'Seen - Normal',
        fourCH: 'Seen - Normal',
        cardiacAxis: 'Seen - Normal',
        aorta: 'Seen - Normal',
        ductalArch: 'Seen - Normal',
        heartSweep: 'Seen - Normal',
        outletCross: 'Seen - Normal',
        aortaRA: 'Seen - Normal',
        lvot: 'Seen - Normal',
        rvot: 'Seen - Normal',
        threeVV: 'Seen - Normal',
        threeCord: 'Seen - Normal',
        situs: 'Seen - Normal',
        levelII: false,
        humeralLength: '',
        hipAngle: '',
        fifthDigits: 'Seen - Normal'
      },
      gestationalAge: {
        ultrasoundEGA: { weeks: '', days: '' },
        ultrasoundEDD: '',
        clinicalEGA: { weeks: '', days: '' },
        clinicalEDD: '',
        lmp: ''
      },
      biophysicalProfile: {
        breathing: '',
        movement: '',
        tone: '',
        fluid: ''
      },
      uaDoppler: {
        fetal: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
        midCord: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' },
        placenta: { psv: '', sdRatio: '', percentile: '', diastolicFlow: 'Present' }
      },
      comments: ''
    });

    // Adjust fetuses array
    setFormData(prev => {
      const currentFetuses = [...prev.fetuses];
      if (newNum > currentFetuses.length) {
        // Add new fetuses
        for (let i = currentFetuses.length; i < newNum; i++) {
          currentFetuses.push(createFetus(i + 1));
        }
      } else if (newNum < currentFetuses.length) {
        // Remove extra fetuses
        currentFetuses.splice(newNum);
      }
      // Reset chorionicity when switching to singleton
      const newChorionicity = newNum === 1 ? '' : prev.chorionicity;
      return { ...prev, examType: newExamType, numberOfFetuses: newNum, chorionicity: newChorionicity, fetuses: currentFetuses };
    });

    // Reset active tab if needed
    if (activeFetusTab >= newNum) {
      setActiveFetusTab(newNum - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const files = await generateReportFiles('Second Trimester OB', formData, facilityInfo);
      await saveReportFiles(files);
      setMessage({ type: 'success', text: describeSavedReport(files) });
      postReportAudit({ apiUrl: process.env.REACT_APP_API_URL, token, examType: 'Second Trimester OB', facilityInfo });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `✗ Error: ${error.message}` 
      });
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });
    const previewWindow = reservePreviewWindow(facilityInfo);
    try {
      const files = await generateReportFiles('Second Trimester OB', formData, facilityInfo);
      await showReportPreview(files, previewWindow);
    } catch (error) {
      abandonPreviewWindow(previewWindow);
      setMessage({ type: 'error', text: `✗ Error: ${error.message}` });
      console.error('Preview error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Wrapper function to make setFormData compatible with PatientInfo component
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClearForm = () => {
    setFormData(getInitialFormData());
    setNumberOfFetuses(1);
    setActiveFetusTab(0);
    setActiveSubTab(0);
    setActiveMaternalTab(0);
    setFocusedAnatomyField(null);
    setFocusedDiastolicField(null);
    setFocusedBPPField(null);
    setShowReferenceRange(false);
    setMessage({ type: '', text: '' });
  };

  return (
    <div>
      <button onClick={onBack} className="back-btn">
        ← Back to Selection
      </button>

      <div className="form-container">
        <h2 style={{ textAlign: 'center', color: 'var(--color-primary)', marginBottom: '30px' }}>
          {formData.examType}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Patient Information */}
          <PatientInfo formData={formData} updateField={updateField} technologists={technologists} />

          {/* Exam Type */}
          <ExamTypeSection
            value={formData.examTypeDetails}
            onChange={(examTypeDetails) => updateField('examTypeDetails', examTypeDetails)}
          />

          {/* Maternal Structures - SHARED */}
          <MaternalStructureTabs
            maternal={formData.maternalStructures}
            onChange={handleMaternalChange}
            activeTab={activeMaternalTab}
            onTabChange={setActiveMaternalTab}
          />

          {/* Number of Fetuses Selection */}
          <div className="form-section">
            <h3>Number of Gestations</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Number of Fetuses:</label>
                <select
                  value={numberOfFetuses}
                  onChange={(e) => handleNumberOfFetusesChange(e.target.value)}
                  style={{ width: '200px', fontSize: '16px', padding: '8px' }}
                >
                  <option value="1">Singleton (1)</option>
                  <option value="2">Twins (2)</option>
                  <option value="3">Triplets (3)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Chorionicity:</label>
                <select
                  value={formData.chorionicity}
                  onChange={(e) => setFormData(prev => ({ ...prev, chorionicity: e.target.value }))}
                  disabled={numberOfFetuses === 1}
                  style={{
                    width: '250px',
                    fontSize: '16px',
                    padding: '8px',
                    backgroundColor: numberOfFetuses === 1 ? 'var(--color-disabled-bg)' : 'var(--color-input-bg)',
                    cursor: numberOfFetuses === 1 ? 'not-allowed' : 'pointer',
                    color: numberOfFetuses === 1 ? 'var(--color-disabled-text)' : 'var(--color-text)'
                  }}
                >
                  <option value="">Select...</option>
                  <option value="Dichorionic-Diamniotic">Dichorionic-Diamniotic</option>
                  <option value="Monochorionic-Diamniotic">Monochorionic-Diamniotic</option>
                  <option value="Monochorionic-Monoamniotic">Monochorionic-Monoamniotic</option>
                  <option value="Indeterminate">Indeterminate</option>
                </select>
              </div>
            </div>
          </div>

          {/* TAB NAVIGATION */}
          <div className="form-section">
            <div style={{
              display: 'flex',
              borderBottom: '2px solid var(--color-primary)',
              marginBottom: '20px'
            }}>
              {formData.fetuses.map((fetus, index) => (
                <button
                  key={fetus.id}
                  type="button"
                  onClick={() => { setActiveFetusTab(index); setActiveSubTab(0); }}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    backgroundColor: activeFetusTab === index ? 'var(--color-primary)' : 'var(--color-surface)',
                    color: activeFetusTab === index ? 'var(--color-card)' : 'var(--color-text)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: activeFetusTab === index ? 'bold' : 'normal',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    marginRight: '4px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Fetus {fetus.id}
                </button>
              ))}
            </div>

            {/* Per-Fetus Content */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--color-surface)',
              borderRadius: '8px',
              minHeight: '400px'
            }}>
              <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>
                Fetus {currentFetus.id} Information
              </h3>

              {/* Subtab Navigation */}
              <div style={{ display: 'flex', borderBottom: '2px solid var(--color-border)', marginBottom: '20px' }}>
                {['Fetal Environment', 'Biometry', 'Anatomy', 'BPP', 'UA Doppler'].map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setActiveSubTab(i)}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      backgroundColor: activeSubTab === i ? 'var(--color-primary)' : 'var(--color-surface)',
                      color: activeSubTab === i ? 'var(--color-card)' : 'var(--color-text-secondary)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: activeSubTab === i ? 'bold' : 'normal',
                      borderTopLeftRadius: '6px',
                      borderTopRightRadius: '6px',
                      marginRight: '3px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Subtab 0: Fetal Environment */}
              {activeSubTab === 0 && <>

              {/* Fetal Environment */}
              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4>Fetal Environment</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Placenta Location:</label>
                    <select
                      value={currentFetus.placentaLocation}
                      onChange={(e) => updateCurrentFetus({ placentaLocation: e.target.value })}
                    >
                      <option value="Anterior">Anterior</option>
                      <option value="Posterior">Posterior</option>
                      <option value="Fundal">Fundal</option>
                      <option value="Right lateral">Right lateral</option>
                      <option value="Left lateral">Left lateral</option>
                      <option value="Low-lying">Low-lying</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Placenta Grade:</label>
                    <select
                      value={currentFetus.placentaGrade}
                      onChange={(e) => updateCurrentFetus({ placentaGrade: e.target.value })}
                    >
                      <option value="0">0</option>
                      <option value="I">I</option>
                      <option value="II">II</option>
                      <option value="III">III</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Placenta Previa:</label>
                    <select
                      value={currentFetus.placentaPrevia}
                      onChange={(e) => updateCurrentFetus({ placentaPrevia: e.target.value })}
                    >
                      <option value="None">None</option>
                      <option value="Low-lying placenta">Low-lying placenta</option>
                      <option value="Marginal previa">Marginal previa</option>
                      <option value="Complete previa">Complete previa</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Umbilical Cord:</label>
                    <select
                      value={currentFetus.umbilicalCord}
                      onChange={(e) => updateCurrentFetus({ umbilicalCord: e.target.value })}
                    >
                      <option value="3 vessel cord">3 vessel cord</option>
                      <option value="2 vessel cord">2 vessel cord</option>
                      <option value="Limited view">Limited view</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Amniotic Fluid:</label>
                    <select
                      value={currentFetus.amnioticFluid}
                      onChange={(e) => updateCurrentFetus({ amnioticFluid: e.target.value })}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Oligohydramnios">Oligohydramnios</option>
                      <option value="Polyhydramnios">Polyhydramnios</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>AFI (cm):</label>
                    <input
                      type="text"
                      value={currentFetus.afi}
                      onChange={(e) => updateCurrentFetus({ afi: e.target.value })}
                      placeholder="Amniotic fluid index"
                    />
                  </div>
                </div>
              </div>

              {/* Fetal Information */}
              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4>Fetal Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Presentation:</label>
                    <select
                      value={currentFetus.presentation}
                      onChange={(e) => updateCurrentFetus({ presentation: e.target.value })}
                    >
                      <option value="Cephalic">Cephalic</option>
                      <option value="Breech">Breech</option>
                      <option value="Transverse">Transverse</option>
                      <option value="Oblique">Oblique</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Maternal Location:</label>
                    <select
                      value={currentFetus.maternalLocation}
                      onChange={(e) => updateCurrentFetus({ maternalLocation: e.target.value })}
                      disabled={numberOfFetuses === 1}
                      style={{
                        backgroundColor: numberOfFetuses === 1 ? 'var(--color-disabled-bg)' : 'var(--color-input-bg)',
                        cursor: numberOfFetuses === 1 ? 'not-allowed' : 'pointer',
                        color: numberOfFetuses === 1 ? 'var(--color-disabled-text)' : 'var(--color-text)'
                      }}
                    >
                      <option value="Maternal Right">Maternal Right</option>
                      <option value="Maternal Left">Maternal Left</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Fetal Heart Rate (BPM):</label>
                    <input
                      type="text"
                      value={currentFetus.fetalHeartRate}
                      onChange={(e) => updateCurrentFetus({ fetalHeartRate: e.target.value })}
                      placeholder="Enter heart rate in BPM"
                    />
                  </div>

                  <div className="form-group">
                    <label>Fetal Gender:</label>
                    <select
                      value={currentFetus.gender}
                      onChange={(e) => updateCurrentFetus({ gender: e.target.value })}
                    >
                      <option value="Not determined">Not determined</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Per-fetus Comments */}
              <div className="form-section">
                <h3>Comments</h3>
                <div className="form-group">
                  <textarea
                    value={currentFetus.comments || ''}
                    onChange={(e) => updateCurrentFetus({ comments: e.target.value })}
                    rows="4"
                    placeholder="Enter any comments or findings specific to this fetus..."
                  />
                </div>
              </div>

              </>}

              {/* Subtab 1: Biometry */}
              {activeSubTab === 1 && <>

              {/* Biometry */}
              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4>Fetal Biometry</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                  {/* LEFT COLUMN - Measurements */}
                  <div>
                    {[['bpd', 'BPD'], ['hc', 'HC'], ['ac', 'AC'], ['fl', 'FL']].map(([key, label]) => {
                      const measure = currentFetus.biometry[key];
                      const setMeasure = (patch) => updateCurrentFetusNested('biometry', {
                        [key]: { ...measure, ...patch }
                      });
                      return (
                        <div className="form-group" key={key}>
                          <label>{label} (cm):</label>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              value={measure.size}
                              onChange={(e) => setMeasure({ size: e.target.value })}
                              placeholder="Size"
                              style={{ width: '80px' }}
                            />
                            <input
                              type="text"
                              value={measure.weeks}
                              onChange={(e) => setMeasure({ weeks: e.target.value.replace(/\D/g, '') })}
                              placeholder="Weeks"
                              style={{ width: '80px' }}
                            />
                            <input
                              type="text"
                              value={measure.days}
                              onChange={(e) => setMeasure({ days: e.target.value.replace(/\D/g, '') })}
                              placeholder="Days"
                              style={{ width: '80px' }}
                            />
                            <input
                              type="text"
                              value={measure.percentile || ''}
                              onChange={(e) => setMeasure({ percentile: e.target.value })}
                              placeholder="Percentile"
                              style={{ width: '100px' }}
                            />
                          </div>
                        </div>
                      );
                    })}

                    <div className="form-group">
                      <label>EFW (grams):</label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={currentFetus.biometry.efw}
                          onChange={(e) => updateCurrentFetusNested('biometry', {
                            efw: e.target.value
                          })}
                          placeholder="Weight"
                          style={{ width: '120px' }}
                        />
                        <input
                          type="text"
                          value={currentFetus.biometry.percentile}
                          onChange={(e) => updateCurrentFetusNested('biometry', {
                            percentile: e.target.value
                          })}
                          placeholder="Percentile"
                          style={{ width: '120px' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* RIGHT COLUMN - Ratios */}
                  <div>
                    <div className="form-group">
                      <label>CI:</label>
                      <input
                        type="text"
                        value={currentFetus.biometry.ci}
                        onChange={(e) => updateCurrentFetusNested('biometry', {
                          ci: e.target.value
                        })}
                        placeholder="Ratio"
                        style={{ width: '90px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label>HC/AC:</label>
                      <input
                        type="text"
                        value={currentFetus.biometry.hcac}
                        onChange={(e) => updateCurrentFetusNested('biometry', {
                          hcac: e.target.value
                        })}
                        placeholder="Ratio"
                        style={{ width: '90px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label>FL/BPD:</label>
                      <input
                        type="text"
                        value={currentFetus.biometry.flbpd}
                        onChange={(e) => updateCurrentFetusNested('biometry', {
                          flbpd: e.target.value
                        })}
                        placeholder="Ratio"
                        style={{ width: '90px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label>FL/HC:</label>
                      <input
                        type="text"
                        value={currentFetus.biometry.flhc}
                        onChange={(e) => updateCurrentFetusNested('biometry', {
                          flhc: e.target.value
                        })}
                        placeholder="Ratio"
                        style={{ width: '90px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label>FL/AC:</label>
                      <input
                        type="text"
                        value={currentFetus.biometry.flac}
                        onChange={(e) => updateCurrentFetusNested('biometry', {
                          flac: e.target.value
                        })}
                        placeholder="Ratio"
                        style={{ width: '90px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Gestational Age */}
              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4>Gestational Age</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Ultrasound Dates:</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={currentFetus.gestationalAge.ultrasoundEGA.weeks}
                        onChange={(e) => handleEGAChange('ultrasoundEGA', 'weeks', e.target.value)}
                        placeholder="Weeks"
                        style={{ width: '80px' }}
                      />
                      <span>weeks</span>
                      <input
                        type="text"
                        value={currentFetus.gestationalAge.ultrasoundEGA.days}
                        onChange={(e) => handleEGAChange('ultrasoundEGA', 'days', e.target.value)}
                        placeholder="Days"
                        style={{ width: '80px' }}
                      />
                      <span style={{ marginRight: '20px' }}>days</span>
                      <span>EDD</span>
                      <input
                        type="text"
                        value={currentFetus.gestationalAge.ultrasoundEDD}
                        onChange={(e) => updateCurrentFetusNested('gestationalAge', {
                          ultrasoundEDD: formatDate(e.target.value)
                        })}
                        placeholder="MM/DD/YYYY"
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Clinical Dates:</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={currentFetus.gestationalAge.clinicalEGA.weeks}
                        onChange={(e) => handleEGAChange('clinicalEGA', 'weeks', e.target.value)}
                        placeholder="Weeks"
                        style={{ width: '80px' }}
                      />
                      <span>weeks</span>
                      <input
                        type="text"
                        value={currentFetus.gestationalAge.clinicalEGA.days}
                        onChange={(e) => handleEGAChange('clinicalEGA', 'days', e.target.value)}
                        placeholder="Days"
                        style={{ width: '80px' }}
                      />
                      <span style={{ marginRight: '20px' }}>days</span>
                      <span>EDD</span>
                      <input
                        type="text"
                        value={currentFetus.gestationalAge.clinicalEDD}
                        onChange={(e) => updateCurrentFetusNested('gestationalAge', {
                          clinicalEDD: formatDate(e.target.value)
                        })}
                        placeholder="MM/DD/YYYY"
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>LMP:</label>
                  <input
                    type="text"
                    value={currentFetus.gestationalAge.lmp}
                    onChange={(e) => updateCurrentFetusNested('gestationalAge', {
                      lmp: formatDate(e.target.value)
                    })}
                    placeholder="Type: 01152025 = 01/15/2025"
                    style={{ width: '200px' }}
                  />
                </div>

                {calculateGestationConcordance() && (
                  <div className="form-group">
                    <label>Concordance:</label>
                    <input
                      type="text"
                      value={calculateGestationConcordance()}
                      readOnly
                      style={{ backgroundColor: 'var(--color-surface)', cursor: 'default' }}
                    />
                  </div>
                )}
              </div>

              </>}

              {/* Subtab 2: Anatomy */}
              {activeSubTab === 2 && <>

              {/* Fetal Anatomy */}
              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h4 style={{ margin: 0 }}>Fetal Anatomy</h4>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <button
                      type="button"
                      onClick={() => setAnatomyDefaults('anatomic')}
                      title="Click to select anatomic survey"
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentFetus.examMode === 'anatomic' ? 'var(--color-primary)' : 'var(--color-surface)',
                        color: currentFetus.examMode === 'anatomic' ? 'var(--color-card)' : 'var(--color-text)',
                        border: currentFetus.examMode === 'anatomic' ? '2px solid var(--color-primary)' : '2px solid transparent',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: currentFetus.examMode === 'anatomic' ? 'bold' : 'normal',
                        boxShadow: currentFetus.examMode === 'anatomic' ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Anatomic Survey
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnatomyDefaults('followup')}
                      title="Click to select follow up exam"
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentFetus.examMode === 'followup' ? 'var(--color-primary)' : 'var(--color-surface)',
                        color: currentFetus.examMode === 'followup' ? 'var(--color-card)' : 'var(--color-text)',
                        border: currentFetus.examMode === 'followup' ? '2px solid var(--color-primary)' : '2px solid transparent',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: currentFetus.examMode === 'followup' ? 'bold' : 'normal',
                        boxShadow: currentFetus.examMode === 'followup' ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Follow Up Exam
                    </button>
                    <button
                      type="button"
                      title="Toggle Level II anatomy fields"
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentFetus.anatomy.levelII ? 'var(--color-success-solid)' : 'var(--color-surface)',
                        color: currentFetus.anatomy.levelII ? 'white' : 'var(--color-text)',
                        border: currentFetus.anatomy.levelII ? '2px solid var(--color-success)' : '2px solid transparent',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: currentFetus.anatomy.levelII ? 'bold' : 'normal',
                        boxShadow: currentFetus.anatomy.levelII ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => updateCurrentFetusNested('anatomy', { levelII: !currentFetus.anatomy.levelII })}
                    >
                      {currentFetus.anatomy.levelII ? 'Level II (On)' : 'Level II'}
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  {/* Column 1 */}
                  <div>
                    <div className="form-group">
                      <label>Nasal Bone:</label>
                      <select
                        value={currentFetus.anatomy.nasalBone}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { nasalBone: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('nasalBone')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('nasalBone', currentFetus.anatomy.nasalBone)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Profile:</label>
                      <select
                        value={currentFetus.anatomy.profile}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { profile: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('profile')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('profile', currentFetus.anatomy.profile)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Nose/Lips:</label>
                      <select
                        value={currentFetus.anatomy.noseLips}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { noseLips: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('noseLips')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('noseLips', currentFetus.anatomy.noseLips)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Face:</label>
                      <select
                        value={currentFetus.anatomy.face}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { face: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('face')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('face', currentFetus.anatomy.face)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Palate:</label>
                      <select
                        value={currentFetus.anatomy.palate}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { palate: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('palate')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('palate', currentFetus.anatomy.palate)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Mandible/Chin:</label>
                      <select
                        value={currentFetus.anatomy.mandibleChin}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { mandibleChin: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('mandibleChin')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('mandibleChin', currentFetus.anatomy.mandibleChin)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Orbits:</label>
                      <select
                        value={currentFetus.anatomy.orbits}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { orbits: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('orbits')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('orbits', currentFetus.anatomy.orbits)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Ventricles:</label>
                      <select
                        value={currentFetus.anatomy.ventricles}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { ventricles: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('ventricles')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('ventricles', currentFetus.anatomy.ventricles)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Choroid Plexus:</label>
                      <select
                        value={currentFetus.anatomy.choroidPlexus}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { choroidPlexus: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('choroidPlexus')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('choroidPlexus', currentFetus.anatomy.choroidPlexus)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Midline Falx:</label>
                      <select
                        value={currentFetus.anatomy.midlineFalx}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { midlineFalx: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('midlineFalx')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('midlineFalx', currentFetus.anatomy.midlineFalx)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>CSP:</label>
                      <select
                        value={currentFetus.anatomy.csp}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { csp: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('csp')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('csp', currentFetus.anatomy.csp)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Cerebellum:</label>
                      <select
                        value={currentFetus.anatomy.cerebellum}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { cerebellum: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('cerebellum')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('cerebellum', currentFetus.anatomy.cerebellum)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Cisterna Magna:</label>
                      <select
                        value={currentFetus.anatomy.cisternaMagna}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { cisternaMagna: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('cisternaMagna')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('cisternaMagna', currentFetus.anatomy.cisternaMagna)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div>
                    <div className="form-group">
                      <label>Nuchal Fold:</label>
                      <select
                        value={currentFetus.anatomy.nuchalFold}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { nuchalFold: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('nuchalFold')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('nuchalFold', currentFetus.anatomy.nuchalFold)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Fetal CI:</label>
                      <select
                        value={currentFetus.anatomy.fetalCI}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { fetalCI: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('fetalCI')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('fetalCI', currentFetus.anatomy.fetalCI)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Bladder:</label>
                      <select
                        value={currentFetus.anatomy.bladder}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { bladder: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('bladder')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('bladder', currentFetus.anatomy.bladder)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Kidneys:</label>
                      <select
                        value={currentFetus.anatomy.kidneys}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { kidneys: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('kidneys')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('kidneys', currentFetus.anatomy.kidneys)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Plac CI:</label>
                      <select
                        value={currentFetus.anatomy.placentaCI}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { placentaCI: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('placentaCI')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('placentaCI', currentFetus.anatomy.placentaCI)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Open Hands:</label>
                      <select
                        value={currentFetus.anatomy.openHands}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { openHands: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('openHands')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('openHands', currentFetus.anatomy.openHands)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Arms:</label>
                      <select
                        value={currentFetus.anatomy.arms}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { arms: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('arms')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('arms', currentFetus.anatomy.arms)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Feet/Ankle:</label>
                      <select
                        value={currentFetus.anatomy.feetAnkle}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { feetAnkle: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('feetAnkle')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('feetAnkle', currentFetus.anatomy.feetAnkle)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Legs:</label>
                      <select
                        value={currentFetus.anatomy.legs}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { legs: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('legs')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('legs', currentFetus.anatomy.legs)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Diaphragm:</label>
                      <select
                        value={currentFetus.anatomy.diaphragm}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { diaphragm: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('diaphragm')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('diaphragm', currentFetus.anatomy.diaphragm)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>RVOT:</label>
                      <select
                        value={currentFetus.anatomy.rvot}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { rvot: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('rvot')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('rvot', currentFetus.anatomy.rvot)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>3VV:</label>
                      <select
                        value={currentFetus.anatomy.threeVV}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { threeVV: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('threeVV')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('threeVV', currentFetus.anatomy.threeVV)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Situs:</label>
                      <select
                        value={currentFetus.anatomy.situs}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { situs: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('situs')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('situs', currentFetus.anatomy.situs)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>
                  </div>

                  {/* Column 3 */}
                  <div>
                    <div className="form-group">
                      <label>Stomach:</label>
                      <select
                        value={currentFetus.anatomy.stomach}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { stomach: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('stomach')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('stomach', currentFetus.anatomy.stomach)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>C-Spine:</label>
                      <select
                        value={currentFetus.anatomy.cSpine}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { cSpine: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('cSpine')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('cSpine', currentFetus.anatomy.cSpine)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>T-Spine:</label>
                      <select
                        value={currentFetus.anatomy.tSpine}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { tSpine: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('tSpine')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('tSpine', currentFetus.anatomy.tSpine)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>L-Spine:</label>
                      <select
                        value={currentFetus.anatomy.lSpine}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { lSpine: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('lSpine')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('lSpine', currentFetus.anatomy.lSpine)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>S-Spine:</label>
                      <select
                        value={currentFetus.anatomy.sSpine}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { sSpine: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('sSpine')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('sSpine', currentFetus.anatomy.sSpine)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>4CH:</label>
                      <select
                        value={currentFetus.anatomy.fourCH}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { fourCH: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('fourCH')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('fourCH', currentFetus.anatomy.fourCH)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Cardiac Axis:</label>
                      <select
                        value={currentFetus.anatomy.cardiacAxis}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { cardiacAxis: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('cardiacAxis')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('cardiacAxis', currentFetus.anatomy.cardiacAxis)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Aorta:</label>
                      <select
                        value={currentFetus.anatomy.aorta}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { aorta: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('aorta')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('aorta', currentFetus.anatomy.aorta)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Ductal Arch:</label>
                      <select
                        value={currentFetus.anatomy.ductalArch}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { ductalArch: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('ductalArch')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('ductalArch', currentFetus.anatomy.ductalArch)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Heart Sweep:</label>
                      <select
                        value={currentFetus.anatomy.heartSweep}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { heartSweep: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('heartSweep')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('heartSweep', currentFetus.anatomy.heartSweep)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Outlet Cross:</label>
                      <select
                        value={currentFetus.anatomy.outletCross}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { outletCross: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('outletCross')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('outletCross', currentFetus.anatomy.outletCross)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Aorta/RA:</label>
                      <select
                        value={currentFetus.anatomy.aortaRA}
                        onChange={(e) => { updateCurrentFetusNested('anatomy', { aortaRA: e.target.value }); setFocusedAnatomyField(null); }}
                        onFocus={() => setFocusedAnatomyField('aortaRA')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('aortaRA', currentFetus.anatomy.aortaRA)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>LVOT:</label>
                      <select
                        value={currentFetus.anatomy.lvot}
                        onChange={(e) => {
                          updateCurrentFetusNested('anatomy', { lvot: e.target.value });
                          setFocusedAnatomyField(null);
                        }}
                        onFocus={() => setFocusedAnatomyField('lvot')}
                        onBlur={() => setFocusedAnatomyField(null)}
                        style={getAnatomyDropdownStyle('lvot', currentFetus.anatomy.lvot)}
                      >
                        <option value="Seen - Normal">Seen - Normal</option>
                        <option value="Not Well Visualized">Not Well Visualized</option>
                        <option value="Abnormal">Abnormal</option>
                        <option value="Not Evaluated">Not Evaluated</option>
                      </select>
                    </div>

                  </div>
                </div>

                {/* Level II anatomy fields */}
                {currentFetus.anatomy.levelII && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--color-surface)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                    <h5 style={{ color: 'var(--color-primary)', marginTop: 0, marginBottom: '15px' }}>Level II Anatomy</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="form-group">
                        <label>Humeral Length:</label>
                        <input
                          type="text"
                          placeholder="mm"
                          value={currentFetus.anatomy.humeralLength}
                          onChange={(e) => updateCurrentFetusNested('anatomy', { humeralLength: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Hip Angle:</label>
                        <input
                          type="text"
                          placeholder="degrees"
                          value={currentFetus.anatomy.hipAngle}
                          onChange={(e) => updateCurrentFetusNested('anatomy', { hipAngle: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Fifth Digits:</label>
                        <select
                          value={currentFetus.anatomy.fifthDigits}
                          onChange={(e) => { updateCurrentFetusNested('anatomy', { fifthDigits: e.target.value }); setFocusedAnatomyField(null); }}
                          onFocus={() => setFocusedAnatomyField('fifthDigits')}
                          onBlur={() => setFocusedAnatomyField(null)}
                          style={getAnatomyDropdownStyle('fifthDigits', currentFetus.anatomy.fifthDigits)}
                        >
                          <option value="Seen - Normal">Seen - Normal</option>
                          <option value="Not Well Visualized">Not Well Visualized</option>
                          <option value="Abnormal">Abnormal</option>
                          <option value="Not Evaluated">Not Evaluated</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              </>}

              {/* Subtab 3: BPP */}
              {activeSubTab === 3 && <>

              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4>Biophysical Profile (BPP)</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Fetal Breathing:</label>
                    <select
                      value={currentFetus.biophysicalProfile.breathing}
                      onChange={(e) => {
                        updateCurrentFetusNested('biophysicalProfile', {
                          breathing: e.target.value
                        });
                        setFocusedBPPField(null);
                      }}
                      onFocus={() => setFocusedBPPField('breathing')}
                      onBlur={() => setFocusedBPPField(null)}
                      style={getBPPDropdownStyle('breathing', currentFetus.biophysicalProfile.breathing)}
                    >
                      <option value="">Select...</option>
                      <option value="Present">Present (2)</option>
                      <option value="Absent">Absent (0)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Fetal Movement:</label>
                    <select
                      value={currentFetus.biophysicalProfile.movement}
                      onChange={(e) => {
                        updateCurrentFetusNested('biophysicalProfile', {
                          movement: e.target.value
                        });
                        setFocusedBPPField(null);
                      }}
                      onFocus={() => setFocusedBPPField('movement')}
                      onBlur={() => setFocusedBPPField(null)}
                      style={getBPPDropdownStyle('movement', currentFetus.biophysicalProfile.movement)}
                    >
                      <option value="">Select...</option>
                      <option value="Present">Present (2)</option>
                      <option value="Absent">Absent (0)</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Fetal Tone:</label>
                    <select
                      value={currentFetus.biophysicalProfile.tone}
                      onChange={(e) => {
                        updateCurrentFetusNested('biophysicalProfile', {
                          tone: e.target.value
                        });
                        setFocusedBPPField(null);
                      }}
                      onFocus={() => setFocusedBPPField('tone')}
                      onBlur={() => setFocusedBPPField(null)}
                      style={getBPPDropdownStyle('tone', currentFetus.biophysicalProfile.tone)}
                    >
                      <option value="">Select...</option>
                      <option value="Present">Present (2)</option>
                      <option value="Absent">Absent (0)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Amniotic Fluid:</label>
                    <select
                      value={currentFetus.biophysicalProfile.fluid}
                      onChange={(e) => {
                        updateCurrentFetusNested('biophysicalProfile', {
                          fluid: e.target.value
                        });
                        setFocusedBPPField(null);
                      }}
                      onFocus={() => setFocusedBPPField('fluid')}
                      onBlur={() => setFocusedBPPField(null)}
                      style={getBPPDropdownStyle('fluid', currentFetus.biophysicalProfile.fluid)}
                    >
                      <option value="">Select...</option>
                      <option value="Adequate">Adequate (2)</option>
                      <option value="Decreased">Decreased (0)</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Total Score:</label>
                  <input
                    type="text"
                    value={`${calculateBPPScore()} / 8`}
                    readOnly
                    style={{ backgroundColor: 'var(--color-surface)', cursor: 'default', fontWeight: 'bold' }}
                  />
                </div>
              </div>

              </>}

              {/* Subtab 4: UA Doppler */}
              {activeSubTab === 4 && <>

              <div className="form-section" style={{ backgroundColor: 'var(--color-card)', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4>UA Doppler</h4>
                
                {/* Fetal */}
                <div style={{ marginBottom: '15px' }}>
                  <h5 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Fetal:</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>PSV (cm/sec):</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.fetal.psv}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          fetal: { ...currentFetus.uaDoppler.fetal, psv: e.target.value }
                        })}
                        placeholder="PSV"
                      />
                    </div>

                    <div className="form-group">
                      <label>S:D Ratio:</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.fetal.sdRatio}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          fetal: { ...currentFetus.uaDoppler.fetal, sdRatio: e.target.value }
                        })}
                        placeholder="S:D"
                      />
                    </div>

                    <div className="form-group">
                      <label>Percentile:</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.fetal.percentile}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          fetal: { ...currentFetus.uaDoppler.fetal, percentile: e.target.value }
                        })}
                        placeholder="%ile"
                      />
                    </div>

                    <div className="form-group">
                      <label>Diastolic Flow:</label>
                      <select
                        value={currentFetus.uaDoppler.fetal.diastolicFlow}
                        onChange={(e) => {
                          updateCurrentFetusNested('uaDoppler', {
                            fetal: { ...currentFetus.uaDoppler.fetal, diastolicFlow: e.target.value }
                          });
                          setFocusedDiastolicField(null);
                        }}
                        onFocus={() => setFocusedDiastolicField('fetal')}
                        onBlur={() => setFocusedDiastolicField(null)}
                        style={getDiastolicFlowStyle('fetal', currentFetus.uaDoppler.fetal.diastolicFlow)}
                      >
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="Reversed">Reversed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Mid Cord */}
                <div style={{ marginBottom: '15px' }}>
                  <h5 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Mid Cord:</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>PSV (cm/sec):</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.midCord.psv}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          midCord: { ...currentFetus.uaDoppler.midCord, psv: e.target.value }
                        })}
                        placeholder="PSV"
                      />
                    </div>

                    <div className="form-group">
                      <label>S:D Ratio:</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.midCord.sdRatio}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          midCord: { ...currentFetus.uaDoppler.midCord, sdRatio: e.target.value }
                        })}
                        placeholder="S:D"
                      />
                    </div>

                    <div className="form-group">
                      <label>Percentile:</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.midCord.percentile}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          midCord: { ...currentFetus.uaDoppler.midCord, percentile: e.target.value }
                        })}
                        placeholder="%ile"
                      />
                    </div>

                    <div className="form-group">
                      <label>Diastolic Flow:</label>
                      <select
                        value={currentFetus.uaDoppler.midCord.diastolicFlow}
                        onChange={(e) => {
                          updateCurrentFetusNested('uaDoppler', {
                            midCord: { ...currentFetus.uaDoppler.midCord, diastolicFlow: e.target.value }
                          });
                          setFocusedDiastolicField(null);
                        }}
                        onFocus={() => setFocusedDiastolicField('midCord')}
                        onBlur={() => setFocusedDiastolicField(null)}
                        style={getDiastolicFlowStyle('midCord', currentFetus.uaDoppler.midCord.diastolicFlow)}
                      >
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="Reversed">Reversed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Placenta */}
                <div style={{ marginBottom: '15px' }}>
                  <h5 style={{ marginBottom: '10px', fontWeight: 'bold' }}>Placenta:</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>PSV (cm/sec):</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.placenta.psv}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          placenta: { ...currentFetus.uaDoppler.placenta, psv: e.target.value }
                        })}
                        placeholder="PSV"
                      />
                    </div>

                    <div className="form-group">
                      <label>S:D Ratio:</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.placenta.sdRatio}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          placenta: { ...currentFetus.uaDoppler.placenta, sdRatio: e.target.value }
                        })}
                        placeholder="S:D"
                      />
                    </div>

                    <div className="form-group">
                      <label>Percentile:</label>
                      <input
                        type="text"
                        value={currentFetus.uaDoppler.placenta.percentile}
                        onChange={(e) => updateCurrentFetusNested('uaDoppler', {
                          placenta: { ...currentFetus.uaDoppler.placenta, percentile: e.target.value }
                        })}
                        placeholder="%ile"
                      />
                    </div>

                    <div className="form-group">
                      <label>Diastolic Flow:</label>
                      <select
                        value={currentFetus.uaDoppler.placenta.diastolicFlow}
                        onChange={(e) => {
                          updateCurrentFetusNested('uaDoppler', {
                            placenta: { ...currentFetus.uaDoppler.placenta, diastolicFlow: e.target.value }
                          });
                          setFocusedDiastolicField(null);
                        }}
                        onFocus={() => setFocusedDiastolicField('placenta')}
                        onBlur={() => setFocusedDiastolicField(null)}
                        style={getDiastolicFlowStyle('placenta', currentFetus.uaDoppler.placenta.diastolicFlow)}
                      >
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="Reversed">Reversed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Reference Range Button */}
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowReferenceRange(!showReferenceRange)}
                    className="add-mass-btn"
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      backgroundColor: 'var(--color-success-solid)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {showReferenceRange ? 'Hide Reference Range' : 'Show Reference Range'}
                  </button>
                </div>

                {/* Tooltip with Image */}
                {showReferenceRange && (
                  <div style={{
                    position: 'relative',
                    marginTop: '20px',
                    padding: '10px',
                    backgroundColor: 'var(--color-card)',
                    border: '2px solid var(--color-primary)',
                    borderRadius: '5px'
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowReferenceRange(false)}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'var(--color-danger-solid)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      ×
                    </button>
                    <img 
                      src="/images/UA_SD_indices.png" 
                      alt="UA S/D Indices Reference" 
                      style={{ width: '100%', maxWidth: '800px', height: 'auto', display: 'block', margin: '0 auto' }}
                    />
                  </div>
                )}
              </div>

              </>}

            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="preview-btn" disabled={isSubmitting} onClick={handlePreview}>
              Preview Report
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Generating Report...' : 'Generate & Save Report'}
            </button>
            <button type="button" className="reset-btn" disabled={isSubmitting} onClick={handleClearForm}>
              Clear Form
            </button>
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default SecondTrimesterMultipleOBForm;
